// Daily freshness for the My Day page: a quote + a colour "vibe" that
// rotate every calendar day, plus a once-a-day "have you checked in?"
// dot driven off localStorage (same pattern as the changelog unread
// dot — see lib/changelog.ts).
//
// Deterministic-by-date: everyone on the team sees the SAME quote/vibe
// on a given day (nice for shared "did you see today's one?" moments),
// but it changes at local midnight. No backend, no API, no RNG drift.

// ── Date seed ────────────────────────────────────────────────────────────────
/** Days since the Unix epoch, in the viewer's LOCAL timezone. Unlike a
 *  YYYYMMDD number this advances by EXACTLY +1 every calendar day (no
 *  month-boundary jumps), which guarantees the quote/scene is always
 *  different from yesterday and walks the full pool before repeating —
 *  "fresh every day", deterministically, no RNG/backend. */
function epochDay(d: Date = new Date()): number {
  // Subtract the local-midnight offset so the boundary is local midnight,
  // not UTC midnight (otherwise it'd flip mid-evening in AEST).
  const local = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.floor(local.getTime() / 86_400_000);
}

/** Local YYYY-MM-DD string — the localStorage value for "last visited". */
function todayStamp(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ── Quotes ───────────────────────────────────────────────────────────────────
// Mix of support-craft wisdom, momentum, and plain good-vibes. Kept
// punchy — this is a banner, not a fortune cookie essay. ~40 entries so
// it doesn't repeat within a working month.
export interface DailyQuote {
  text: string;
  author: string;
}

const QUOTES: DailyQuote[] = [
  { text: "Every ticket is a person having a worse day than you. Be the turn-around.", author: 'Support truth' },
  { text: "Done is better than perfect. Replied-to is better than drafted.", author: 'The queue' },
  { text: "Start where you are. Use what you have. Do what you can.", author: 'Arthur Ashe' },
  { text: "The way to get started is to quit talking and begin doing.", author: 'Walt Disney' },
  { text: "Small daily improvements are the key to staggering long-term results.", author: 'Unknown' },
  { text: "You don't have to be great to start, but you have to start to be great.", author: 'Zig Ziglar' },
  { text: "A clear inbox is a clear mind. Chip away.", author: 'Future you' },
  { text: "Kindness is a language the frustrated client understands instantly.", author: 'Support truth' },
  { text: "It always seems impossible until it's done.", author: 'Nelson Mandela' },
  { text: "Slow is smooth, smooth is fast. Read the ticket twice.", author: 'Unknown' },
  { text: "The best way to predict the future is to create it.", author: 'Peter Drucker' },
  { text: "Energy and persistence conquer all things.", author: 'Benjamin Franklin' },
  { text: "One reply at a time. That's the whole game.", author: 'The queue' },
  { text: "Make each response one you'd be happy to receive.", author: 'Support truth' },
  { text: "Great things are done by a series of small things brought together.", author: 'Van Gogh' },
  { text: "Don't watch the clock; do what it does. Keep going.", author: 'Sam Levenson' },
  { text: "Today's effort is tomorrow's resolved-and-archived.", author: 'Future you' },
  { text: "Quality is not an act, it is a habit.", author: 'Aristotle' },
  { text: "The client doesn't remember the wait. They remember how you made it right.", author: 'Support truth' },
  { text: "Action is the foundational key to all success.", author: 'Pablo Picasso' },
  { text: "Be so good they can't ignore you. Then be kind too.", author: 'Unknown' },
  { text: "Progress, not perfection. Then progress again.", author: 'Unknown' },
  { text: "Either you run the day or the day runs you.", author: 'Jim Rohn' },
  { text: "Patience and a calm reply de-escalate faster than any policy.", author: 'Support truth' },
  { text: "Well done is better than well said.", author: 'Benjamin Franklin' },
  { text: "Start strong, finish stronger. The middle takes care of itself.", author: 'Unknown' },
  { text: "You can't go back and change the start, but you can start where you are and change the ending.", author: 'C. S. Lewis' },
  { text: "The secret of getting ahead is getting started.", author: 'Mark Twain' },
  { text: "Treat every reply like it's the only thing the client will read about you.", author: 'Support truth' },
  { text: "Discipline is choosing between what you want now and what you want most.", author: 'Abraham Lincoln' },
  { text: "Focus on being productive instead of busy.", author: 'Tim Ferriss' },
  { text: "A little progress each day adds up to big results.", author: 'Unknown' },
  { text: "Be the reason someone's day got unexpectedly better.", author: 'Support truth' },
  { text: "The expert in anything was once a beginner.", author: 'Helen Hayes' },
  { text: "Hard things are hard. Do them anyway, then they're done.", author: 'The queue' },
  { text: "Calm is a superpower. Especially in the inbox.", author: 'Unknown' },
  { text: "Success is the sum of small efforts repeated day in and day out.", author: 'Robert Collier' },
  { text: "Show up, be useful, be kind. Repeat.", author: 'Support truth' },
  { text: "What you do today can improve all your tomorrows.", author: 'Ralph Marston' },
  { text: "Make today so good that yesterday gets jealous.", author: 'Unknown' },
];

/** The quote for today. Advances one quote per day and cycles the whole
 *  pool (so no repeat for ~40 days). The `* 3` stride de-correlates it
 *  from the scene rotation so the photo and quote don't move in
 *  lockstep — still fully deterministic per local day. */
export function getDailyQuote(d: Date = new Date()): DailyQuote {
  const i = ((epochDay(d) * 3) % QUOTES.length + QUOTES.length) % QUOTES.length;
  return QUOTES[i];
}

// ── Scene (daily scenery photo) ──────────────────────────────────────────────
// Rotates a calm scenery photo behind the hero each day so the page
// literally looks different when you open it. Photos are bundled in
// `public/scenery/` (self-hosted → reliable, edge-cached by Cloudflare,
// no third-party dependency, no API key). The hero applies a dark
// overlay on top so white text stays readable on ANY photo, and a dark
// fallback colour shows if the image is slow/offline. Neutral nature
// imagery deliberately replaces the old bright gradient — it doesn't
// fight the app's restrained monochrome theme / brand.
export interface DailyScene {
  /** Path under /public — served same-origin from Cloudflare. */
  src: string;
  /** A big friendly emoji for the hero, loosely matched to the scene. */
  emoji: string;
  /** Short alt text. */
  alt: string;
}

const SCENES: DailyScene[] = [
  { src: '/scenery/mountain-lake.jpg',  emoji: '🏔️', alt: 'Still mountain lake at dawn' },
  { src: '/scenery/sunbeam-forest.jpg', emoji: '🌲', alt: 'Sunbeams through a forest' },
  { src: '/scenery/foggy-forest.jpg',   emoji: '🌫️', alt: 'Misty forest morning' },
  { src: '/scenery/forest-path.jpg',    emoji: '🌳', alt: 'Sunlit forest path' },
  { src: '/scenery/lake-mountains.jpg', emoji: '⛰️', alt: 'Lake beneath mountains' },
  { src: '/scenery/green-hills.jpg',    emoji: '🌄', alt: 'Rolling green hills' },
  { src: '/scenery/rolling-hills.jpg',  emoji: '🍃', alt: 'Soft rolling hills' },
  { src: '/scenery/valley.jpg',         emoji: '🏞️', alt: 'Wide open valley' },
  { src: '/scenery/starry-peak.jpg',    emoji: '🌌', alt: 'Starry sky over a peak' },
  { src: '/scenery/coastal.jpg',        emoji: '🌊', alt: 'Calm coastline' },
];

export function getDailyScene(d: Date = new Date()): DailyScene {
  return SCENES[epochDay(d) % SCENES.length];
}

// ── Greeting ─────────────────────────────────────────────────────────────────
/** Time-of-day greeting with a tiny bit of daily variation so it isn't
 *  word-for-word identical every morning. */
export function getGreeting(name: string, d: Date = new Date()): string {
  const h = d.getHours();
  const base =
    h < 5 ? 'Burning the midnight oil' :
    h < 12 ? 'Good morning' :
    h < 17 ? 'Good afternoon' :
    h < 21 ? 'Good evening' :
    'Winding down';
  return `${base}, ${name}`;
}

/** A short, upbeat one-liner that reacts to how the day's going.
 *  Keeps the banner feeling alive rather than a static quote box. */
export function getMomentumLine(resolvedThisWeek: number, atRisk: number): string {
  if (atRisk > 0) {
    return atRisk === 1
      ? "One ticket needs you first — knock it out and the day's yours."
      : `${atRisk} tickets are racing the clock — let's clear the runway.`;
  }
  if (resolvedThisWeek >= 15) return `${resolvedThisWeek} resolved this week — you're on fire 🔥`;
  if (resolvedThisWeek >= 5) return `${resolvedThisWeek} resolved this week — keep the streak alive.`;
  if (resolvedThisWeek > 0) return `${resolvedThisWeek} down this week — momentum is momentum.`;
  return 'Clean slate. Make today the one that sets the tone.';
}

// ── Once-a-day visit dot ─────────────────────────────────────────────────────
// Mirrors lib/changelog.ts: a localStorage stamp of the last day the
// user opened My Day. If today's stamp differs, the nav shows a red dot
// nudging them to check in. Wiping localStorage just shows the dot
// again — harmless.
export const MYDAY_LAST_VISIT_KEY = 'pa-myday-last-visit';

/** True when the user hasn't opened My Day yet today. Drives the nav dot. */
export function hasUnvisitedMyDayToday(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(MYDAY_LAST_VISIT_KEY) !== todayStamp();
  } catch {
    return false;
  }
}

/** Call on My Day mount — stamps today so the nav dot clears. */
export function markMyDayVisitedToday(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(MYDAY_LAST_VISIT_KEY, todayStamp());
  } catch {
    // ignore — private mode / quota
  }
}
